export const DEMO_CLAIM_MINT_STATUS = {
  PENDING: "PENDING",
  CONSUMED: "CONSUMED",
  REVOKED: "REVOKED",
  EXPIRED: "EXPIRED",
} as const;

export const DEMO_CLAIM_MINT_STATUSES = [
  DEMO_CLAIM_MINT_STATUS.PENDING,
  DEMO_CLAIM_MINT_STATUS.CONSUMED,
  DEMO_CLAIM_MINT_STATUS.REVOKED,
  DEMO_CLAIM_MINT_STATUS.EXPIRED,
] as const;

export const DEMO_ORGANIZATION_STATUS = {
  PERMANENT: "permanent",
  ACTIVE: "active",
  EXPIRED: "expired",
} as const;

export const DEMO_ORGANIZATION_STATUSES = [
  DEMO_ORGANIZATION_STATUS.PERMANENT,
  DEMO_ORGANIZATION_STATUS.ACTIVE,
  DEMO_ORGANIZATION_STATUS.EXPIRED,
] as const;

export const DEMO_CLAIM_TOKEN_PREFIX = "demo_" as const;

/** Default demo org lifetime from claim (days). */
export const DEMO_ORGANIZATION_DEFAULT_DURATION_DAYS = 10;

/** Default claim-link validity from mint (days). */
export const DEMO_CLAIM_MINT_DEFAULT_EXPIRY_DAYS = 30;

/** Minimum KV TTL seconds when writing a demo apiToken. */
export const DEMO_ORGANIZATION_MIN_TOKEN_TTL_SECONDS = 60;

/** Soft-delete demo orgs this many days after demoExpiresAt (lazy cleanup on claim). */
export const DEMO_ORGANIZATION_STALE_RETENTION_DAYS = 30;

/** Max stale demo orgs soft-deleted per claim cleanup pass. */
export const DEMO_ORGANIZATION_STALE_CLEANUP_BATCH_SIZE = 50;

export const DEMO_ORGANIZATION_TEMPLATE_NOT_FOUND_ERROR = "Demo template not found.";

export const DEMO_ORGANIZATION_TEMPLATE_NAME_REQUIRED_ERROR = "Template name is required.";

export const DEMO_ORGANIZATION_DEFAULT_ORG_NAME_REQUIRED_ERROR =
  "Default organization name is required.";

export const DEMO_ORGANIZATION_DEFAULT_PROJECT_NAME_REQUIRED_ERROR =
  "Default project name is required.";

export const DEMO_ORGANIZATION_MARKET_REQUIRED_ERROR = "Market is required.";

export const DEMO_ORGANIZATION_MARKET_NOT_FOUND_ERROR = "Market not found.";

export const DEMO_ORGANIZATION_INVALID_MAX_LOCATIONS_ERROR =
  "Max locations must be a positive integer.";

export const DEMO_ORGANIZATION_INVALID_DURATION_ERROR =
  "Demo duration days must be a positive integer.";

export const DEMO_ORGANIZATION_INVALID_LOCATION_ERROR =
  "One or more seeded locations are invalid.";

export const DEMO_ORGANIZATION_LOCATION_TYPE_NOT_FOUND_ERROR =
  "One or more location types are invalid.";

export const DEMO_LOCATION_NOT_FOUND_ERROR = "Demo location not found.";

export const DEMO_LOCATION_NAME_REQUIRED_ERROR = "Location name is required.";

export const DEMO_LOCATION_TYPE_REQUIRED_ERROR = "Location type is required.";

export const DEMO_LOCATION_TIMEZONE_REQUIRED_ERROR = "Timezone is required.";

export const DEMO_LOCATION_COORDS_INVALID_ERROR =
  "Latitude and longitude must be valid numbers.";

export const DEMO_LOCATION_ATTACHED_ERROR =
  "Demo location is attached to one or more templates and cannot be deleted.";

export const DEMO_LOCATION_IDS_INVALID_ERROR =
  "One or more demo location ids are invalid or deleted.";

export const DEMO_CLAIM_MINT_INVALID_EMAIL_ERROR = "Invitee email is invalid.";

export const DEMO_CLAIM_MINT_MISSING_EMAIL_ERROR = "Email is required.";

export const DEMO_CLAIM_MINT_INVALID_EXPIRES_AT_ERROR = "Claim expiration must be in the future.";

export const DEMO_CLAIM_MINT_NOT_FOUND_ERROR = "Demo claim mint not found.";

export const DEMO_CLAIM_MINT_REVOKED_ERROR = "This demo claim link has been revoked.";

export const DEMO_CLAIM_MINT_EXPIRED_ERROR = "This demo claim link has expired.";

export const DEMO_CLAIM_MINT_ALREADY_CONSUMED_ERROR = "This demo claim link has already been used.";

export const DEMO_CLAIM_MINT_EMAIL_MISMATCH_ERROR =
  "Your account email does not match this demo claim. Sign in with the invited email address.";

export const DEMO_CLAIM_MINT_MISSING_SESSION_EMAIL_ERROR =
  "Your account has no email address. Add an email to your profile before claiming a demo.";

export const DEMO_CLAIM_MINT_UNAUTHORIZED_ERROR = "You must be signed in to claim this demo.";

/** Thrown when concurrent claimers race on the same pending mint. */
export const DEMO_CLAIM_RACE_ERROR = "claim_race";

export const DEMO_CLAIM_MINT_APP_URL_MISSING_ERROR =
  "Demo claim links are unavailable (application URL is not configured).";

export const DEMO_CLAIM_MINT_NOT_PENDING_ERROR = "Only pending demo claim mints can be revoked.";

export const DEMO_ORGANIZATION_NOT_DEMO_ERROR = "Organization is not a demo organization.";

export const DEMO_ORGANIZATION_NOT_FOUND_ERROR = "Organization not found.";

export const DEMO_ORGANIZATION_EXPIRED_MESSAGE =
  "This demo organization has expired. Contact Infoplaza to continue.";

export const DEMO_ORGANIZATION_CREATE_FAILED_ERROR =
  "Could not create demo organization. Try again or contact support.";

export const DEMO_ORGANIZATION_INVALID_EXPIRES_AT_ERROR =
  "Demo expiration must be a valid date and time.";

export const DEMO_ORGANIZATION_EXPIRES_AT_NOT_FUTURE_ERROR =
  "Demo expiration must be in the future.";

export const DEMO_ORGANIZATION_KV_SYNC_FAILED_ERROR =
  "Could not sync organization credentials. Try again or contact support.";

export const DEMO_ORGANIZATION_CONVERT_ROLLBACK_AUDIT_SUMMARY = (organizationName: string) =>
  `Rolled back conversion of demo organization "${organizationName}" after KV sync failure`;

export const DEMO_ORGANIZATION_EXPIRES_AT_ROLLBACK_AUDIT_SUMMARY = (organizationName: string) =>
  `Rolled back demo expiration update for "${organizationName}" after KV sync failure`;

export const DEMO_ORGANIZATION_TEMPLATE_IN_USE_ERROR =
  "Demo template is used by one or more active demo organizations and cannot be deleted.";

export const DEMO_ORGANIZATION_TEMPLATE_HAS_PENDING_MINTS_ERROR =
  "Demo template has pending claim links and cannot be deleted.";

export const DEMO_ORGANIZATION_INVALID_MAX_PROJECTS_ERROR =
  "Max projects must be a positive integer.";

export const DEMO_CLAIM_INVALID_LINK_MESSAGE = "This demo claim link is invalid.";

export const DEMO_CLAIM_INCOMPLETE_ACCOUNT_MESSAGE =
  "Your account setup is incomplete. Sign out and sign in again to claim this demo.";

export const DEMO_CLAIM_CREATING_TITLE = "Creating demo organization";

export const buildDemoClaimCreatingMessage = (
  organizationName: string,
  demoDurationDays: number,
): string =>
  `Setting up "${organizationName}" for ${demoDurationDays} days. This only takes a moment…`;

export const DEMO_CLAIM_UNAVAILABLE_TITLE = "Demo claim unavailable";

export const DEMO_CLAIM_SIGN_IN_DIFFERENT_ACCOUNT_LABEL = "Sign in with a different account";

export const DEMO_CLAIM_GO_TO_DASHBOARD_LABEL = "Go to dashboard";

export const DEMO_ORGANIZATION_SIGN_OUT_LABEL = "Sign out";

export const DEMO_ORGANIZATION_SIGN_OUT_PATH = "/logout";

export const DEMO_ORGANIZATION_BANNER_LABEL = "Demo";

export const DEMO_ORGANIZATION_EXPIRED_TITLE = "Demo expired";

export const DEMO_ORGANIZATION_TIME_LEFT_EXPIRED = "Expired";

/**
 * Client countdown refresh interval while a demo is active.
 * Banner/`isExpired` UI may lag wall-clock expiry by up to one tick; that lag is
 * intentional and tolerated — server-side gates enforce real access control.
 */
export const DEMO_COUNTDOWN_TICK_MS = 60_000;

/** Server/proxy redirect target when the active demo organization is expired. */
export const DEMO_PATH_PREFIX = "/demo";

export const DEMO_ORGANIZATION_EXPIRED_PATH = `${DEMO_PATH_PREFIX}/expired`;

export const DEMO_REVOKE_EXPIRED_TOKEN_PATH = "/api/demo/revoke-expired-token";

export const DEMO_ORGANIZATION_SEEDED_LOCATIONS_EXCEED_MAX_ERROR =
  "Seeded locations cannot exceed max locations.";

export const DEMO_CLAIM_DASHBOARD_PATH = "/dashboard";

export const DEMO_CLAIM_PATH_PREFIX = `${DEMO_PATH_PREFIX}/claim`;

export const DEMO_LOGIN_PATH_PREFIX = `${DEMO_PATH_PREFIX}/login`;

export const buildDemoClaimPath = (rawToken: string): string =>
  `${DEMO_CLAIM_PATH_PREFIX}/${encodeURIComponent(rawToken)}`;

export const buildDemoLoginPath = (rawToken: string): string =>
  `${DEMO_LOGIN_PATH_PREFIX}/${encodeURIComponent(rawToken)}`;

export const DEMO_LOGIN_TITLE = "Welcome to your Infoplaza demo";

export const buildDemoLoginTitle = (
  firstName?: string | null,
  lastName?: string | null,
): string => {
  const name = [firstName, lastName]
    .map((part) => part?.trim())
    .filter((part): part is string => Boolean(part))
    .join(" ");
  if (!name) {
    return DEMO_LOGIN_TITLE;
  }
  return `Welcome ${name} to your Infoplaza demo`;
};

export const DEMO_LOGIN_EXPLANATION =
  "This link opens a time-limited demo organization so you can explore weather intelligence before going live.";

export const buildDemoLoginOrgDetail = (
  organizationName: string,
  demoDurationDays: number,
): string =>
  `You will get access to "${organizationName}" for ${demoDurationDays} days after you sign in.`;

export const DEMO_LOGIN_EMAIL_INSTRUCTION =
  "Sign in or create an account using the same email address you provided when requesting this demo.";

export const buildDemoLoginEmailCallout = (email: string): string =>
  `Use this email: ${email}`;

export const DEMO_LOGIN_SIGN_IN_HEADING = "Continue to your demo";

export const DEMO_LOGIN_SIGN_IN_SUBTEXT =
  "Sign in with your Infoplaza account, or create one if you do not have one yet.";

export const DEMO_LOGIN_SECURED_LABEL = "Secure access";

export const DEMO_LOGIN_ENTERPRISE_SSO_LABEL = "Enterprise SSO";
